import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Subscription, filter, finalize } from 'rxjs';
import { CalendarService } from '../../core/localization/calendar.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicEntityRecordsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import {
  DynamicEntityDetail,
  DynamicField,
  DynamicFieldOption,
  DynamicRecord,
  DynamicRecordFilter,
  DynamicRecordFilterOperator,
  DynamicRecordSort,
  DynamicValidationIssue
} from './dynamic.models';

interface FilterRow {
  id: string;
  fieldName: string | null;
  operator: DynamicRecordFilterOperator | null;
  value: string | number | boolean | null;
}

interface LookupTargetCache {
  labelFieldName: string | null;
  options: DynamicFieldOption[];
  labelById: Map<string, string>;
}

const TEXT_FIELD_TYPES = new Set(['Text', 'LongText', 'Email', 'Phone', 'Url']);
const NUMERIC_FIELD_TYPES = new Set(['Integer', 'Decimal', 'Money']);
const DATE_FIELD_TYPES = new Set(['Date', 'DateTime']);
const READ_ONLY_FIELD_TYPES = new Set(['Guid', 'FileReference', 'Json']);
const MAX_TABLE_COLUMNS = 8;
const DEFAULT_PAGE_SIZE = 25;
const NOW_DEFAULT_TOKEN = '__now__';

function operatorNeedsValue(operator: DynamicRecordFilterOperator): boolean {
  return operator !== 'isnull' && operator !== 'isnotnull';
}

function operatorsForFieldType(fieldType: string): DynamicRecordFilterOperator[] {
  if (TEXT_FIELD_TYPES.has(fieldType)) {
    return ['eq', 'neq', 'contains', 'startsWith', 'isnull', 'isnotnull'];
  }
  if (NUMERIC_FIELD_TYPES.has(fieldType)) {
    return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'];
  }
  if (fieldType === 'Boolean') {
    return ['eq', 'isnull', 'isnotnull'];
  }
  if (DATE_FIELD_TYPES.has(fieldType)) {
    return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'];
  }
  if (fieldType === 'Select' || fieldType === 'MultiSelect' || fieldType === 'Lookup') {
    return ['eq', 'neq', 'isnull', 'isnotnull'];
  }
  return ['eq', 'neq', 'isnull', 'isnotnull'];
}

function pickLabelField(fields: DynamicField[]): DynamicField | null {
  return fields
    .filter(field => TEXT_FIELD_TYPES.has(field.fieldType))
    .sort((left, right) => left.displayOrder - right.displayOrder)[0] ?? null;
}

function buildRecordLabel(record: DynamicRecord, labelField: DynamicField | null, fields: DynamicField[]): string {
  if (labelField) {
    const value = record.values[labelField.name];
    if (value != null && `${value}`.trim()) {
      return `${value}`;
    }
  }

  for (const field of fields.filter(item => TEXT_FIELD_TYPES.has(item.fieldType)).sort((a, b) => a.displayOrder - b.displayOrder)) {
    const value = record.values[field.name];
    if (value != null && `${value}`.trim()) {
      return `${value}`;
    }
  }

  return record.id.slice(0, 8);
}

function selectTableColumns(fields: DynamicField[]): DynamicField[] {
  const sorted = [...fields].sort((left, right) => left.displayOrder - right.displayOrder);
  const required = sorted.filter(field => field.isRequired && !READ_ONLY_FIELD_TYPES.has(field.fieldType));
  const optional = sorted.filter(field => !field.isRequired && !READ_ONLY_FIELD_TYPES.has(field.fieldType) && field.fieldType !== 'LongText');
  const longText = sorted.filter(field => field.fieldType === 'LongText' && !field.isRequired);

  const selected: DynamicField[] = [];
  for (const field of [...required, ...optional, ...longText]) {
    if (selected.some(item => item.name === field.name)) {
      continue;
    }
    selected.push(field);
    if (selected.length >= MAX_TABLE_COLUMNS) {
      break;
    }
  }

  return selected;
}

function extractValidationIssues(error: unknown): DynamicValidationIssue[] {
  if (!(error instanceof HttpErrorResponse)) {
    return [];
  }

  const body = error.error as Record<string, unknown> | null;
  const rawIssues = body?.['issues'] ?? body?.['Issues'];
  if (!Array.isArray(rawIssues)) {
    return [];
  }

  return rawIssues
    .map(item => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const issue = item as Record<string, unknown>;
      const fieldKey = typeof issue['fieldKey'] === 'string'
        ? issue['fieldKey']
        : typeof issue['FieldKey'] === 'string'
          ? issue['FieldKey']
          : '';
      const errorCode = typeof issue['errorCode'] === 'string'
        ? issue['errorCode']
        : typeof issue['ErrorCode'] === 'string'
          ? issue['ErrorCode']
          : '';
      return fieldKey && errorCode ? { fieldKey, errorCode } : null;
    })
    .filter((item): item is DynamicValidationIssue => item !== null);
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-records',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    FormsModule,
    RouterLink,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    TextareaModule
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page
      [title]="pageTitle()"
      [description]="entity()?.displayName ?? ''">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.record.info.title'"
        [descriptionKey]="'dynamic.record.info.description'"
        [bulletKeys]="[
          'dynamic.record.info.bullets.validation',
          'dynamic.record.info.bullets.paging',
          'dynamic.record.info.bullets.export'
        ]" />

      @if (!canViewRecords()) {
        <ain-state type="empty" [title]="t('dynamic.record.title')" [message]="t('common.noPermission')" />
      } @else if (initialLoading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (entityError()) {
        <ain-state type="error" [title]="t('dynamic.record.errorLoad')" [message]="entityError() ?? ''" />
      } @else if (entity(); as ent) {
        <section class="panel">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/dynamic/entities', entityId]">{{ t('dynamic.record.backToDesigner') }}</a>
            <div class="toolbar-actions">
              @if (canCreateRecord()) {
                <button pButton type="button" [label]="t('dynamic.record.create')" (click)="openCreateDialog()"></button>
              }
              @if (canExportRecords()) {
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  icon="pi pi-download"
                  [label]="t('dynamic.record.export')"
                  [disabled]="exporting()"
                  (click)="exportCsv()"></button>
              }
              <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="refreshRecords()"></button>
              <span class="live-status" [class]="liveUpdateService.connectionState()">
                {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
              </span>
            </div>
          </div>

          <div class="filter-toggle">
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="showFilters() ? t('dynamic.record.hideFilters') : t('dynamic.record.showFilters')"
              (click)="toggleFilters()"></button>
          </div>

          @if (showFilters()) {
            <section class="filters card">
              <h3>{{ t('dynamic.record.filter') }}</h3>
              @for (row of filterRows(); track row.id) {
                <div class="filter-row">
                  <ain-form-field [label]="t('dynamic.record.filterField')">
                    <p-select
                      [options]="filterableFields()"
                      optionLabel="displayName"
                      optionValue="name"
                      [(ngModel)]="row.fieldName"
                      [showClear]="true"
                      appendTo="body"
                      (ngModelChange)="onFilterFieldChange(row)" />
                  </ain-form-field>

                  <ain-form-field [label]="t('dynamic.record.filterOperator')">
                    <p-select
                      [options]="operatorOptions(row.fieldName)"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="row.operator"
                      [disabled]="!row.fieldName"
                      appendTo="body" />
                  </ain-form-field>

                  @if (row.fieldName && row.operator && operatorNeedsValue(row.operator)) {
                    <ain-form-field [label]="t('dynamic.record.filterValue')">
                      @if (filterFieldType(row.fieldName); as fieldType) {
                        @switch (fieldType) {
                          @case ('Boolean') {
                            <p-select
                              [options]="booleanOptions()"
                              optionLabel="label"
                              optionValue="value"
                              [(ngModel)]="row.value"
                              appendTo="body" />
                          }
                          @case ('Integer') {
                            <p-inputnumber [(ngModel)]="row.value" [useGrouping]="false" />
                          }
                          @case ('Decimal') {
                            <p-inputnumber [(ngModel)]="row.value" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="6" />
                          }
                          @case ('Money') {
                            <p-inputnumber [(ngModel)]="row.value" mode="currency" currency="USD" locale="en-US" />
                          }
                          @case ('Date') {
                            <ain-date-input [(ngModel)]="row.value" />
                          }
                          @case ('DateTime') {
                            <ain-date-input [(ngModel)]="row.value" />
                          }
                          @case ('Select') {
                            <p-select
                              [options]="fieldOptions(row.fieldName)"
                              optionLabel="label"
                              optionValue="value"
                              [(ngModel)]="row.value"
                              [showClear]="true"
                              appendTo="body" />
                          }
                          @case ('Lookup') {
                            <p-select
                              [options]="lookupOptionsForField(row.fieldName)"
                              optionLabel="label"
                              optionValue="value"
                              [(ngModel)]="row.value"
                              [showClear]="true"
                              appendTo="body" />
                          }
                          @default {
                            <input pInputText type="text" [(ngModel)]="row.value" />
                          }
                        }
                      }
                    </ain-form-field>
                  }

                  <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="removeFilterRow(row.id)"></button>
                </div>
              }

              <div class="filter-actions">
                <button pButton type="button" severity="secondary" [label]="t('dynamic.record.addFilter')" (click)="addFilterRow()"></button>
                <button pButton type="button" [label]="t('dynamic.record.applyFilters')" (click)="applyFilters()"></button>
                <button pButton type="button" severity="secondary" [label]="t('dynamic.record.clearFilters')" (click)="clearFilters()"></button>
              </div>
            </section>
          }

          <p-table
            [value]="records()"
            dataKey="id"
            [paginator]="true"
            [rows]="pageSize"
            [first]="tableFirst()"
            [totalRecords]="totalCount()"
            [lazy]="true"
            [loading]="tableLoading()"
            [sortField]="sortField()"
            [sortOrder]="sortOrder()"
            (onLazyLoad)="onLazyLoad($event)"
            [rowsPerPageOptions]="[10, 25, 50]"
            [tableStyle]="{ 'min-width': '48rem' }">
            <ng-template pTemplate="header">
              <tr>
                @for (column of tableColumns(); track column.name) {
                  <th [pSortableColumn]="column.name">
                    {{ column.displayName }}
                    <p-sortIcon [field]="column.name" />
                  </th>
                }
                <th style="width: 120px">{{ t('dynamic.record.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr class="clickable-row" (click)="openEditDialog(row)">
                @for (column of tableColumns(); track column.name) {
                  <td>{{ formatCellValue(row, column) }}</td>
                }
                <td class="row-actions" (click)="$event.stopPropagation()">
                  @if (canUpdateRecord()) {
                    <button pButton type="button" size="small" severity="secondary" [label]="t('common.open')" (click)="openEditDialog(row)"></button>
                  }
                  @if (canDeleteRecord()) {
                    <button pButton type="button" size="small" severity="danger" [label]="t('common.delete')" (click)="confirmDelete(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="tableColumns().length + 1">{{ t('dynamic.record.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (listError()) {
            <p class="error">{{ listError() }}</p>
          }
        </section>
      }
    </ain-page>

    <p-dialog
      [header]="dialogMode() === 'create' ? t('dynamic.record.create') : t('dynamic.record.edit')"
      [(visible)]="recordDialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(760px, 96vw)' }"
      (onHide)="closeDialog()">
      @if (entity()) {
        <div class="record-form">
          @for (field of sortedFields(); track field.id) {
            @if (isReadOnlyField(field)) {
              <ain-form-field [label]="field.displayName" [hint]="t('dynamic.record.readOnly')">
                <input pInputText [ngModel]="readOnlyValue(field)" [disabled]="true" />
              </ain-form-field>
            } @else {
              <ain-form-field
                [label]="fieldLabel(field)"
                [error]="fieldErrors()[field.name] || ''">
                @switch (field.fieldType) {
                  @case ('LongText') {
                    <textarea pTextarea rows="4" [(ngModel)]="formValues()[field.name]" [attr.maxlength]="textMaxLength(field)"></textarea>
                  }
                  @case ('Integer') {
                    <p-inputnumber
                      [(ngModel)]="formValues()[field.name]"
                      [useGrouping]="false"
                      [min]="field.numericMin ?? undefined"
                      [max]="field.numericMax ?? undefined" />
                  }
                  @case ('Decimal') {
                    <p-inputnumber
                      [(ngModel)]="formValues()[field.name]"
                      mode="decimal"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="6"
                      [min]="field.numericMin ?? undefined"
                      [max]="field.numericMax ?? undefined" />
                  }
                  @case ('Money') {
                    <p-inputnumber
                      [(ngModel)]="formValues()[field.name]"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      [min]="field.numericMin ?? undefined"
                      [max]="field.numericMax ?? undefined" />
                  }
                  @case ('Boolean') {
                    <p-select
                      [options]="booleanOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="formValues()[field.name]"
                      appendTo="body" />
                  }
                  @case ('Date') {
                    <ain-date-input [(ngModel)]="formValues()[field.name]" />
                  }
                  @case ('DateTime') {
                    <ain-date-input [(ngModel)]="formValues()[field.name]" />
                  }
                  @case ('Select') {
                    <p-select
                      [options]="field.options"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="formValues()[field.name]"
                      [showClear]="!field.isRequired"
                      appendTo="body" />
                  }
                  @case ('MultiSelect') {
                    <p-multiSelect
                      [options]="field.options"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="formValues()[field.name]"
                      appendTo="body" />
                  }
                  @case ('Lookup') {
                    <p-select
                      [options]="lookupOptionsForField(field.name)"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="formValues()[field.name]"
                      [showClear]="!field.isRequired"
                      appendTo="body" />
                  }
                  @default {
                    <input
                      pInputText
                      [(ngModel)]="formValues()[field.name]"
                      [attr.maxlength]="textMaxLength(field)"
                      [attr.inputmode]="inputMode(field.fieldType)"
                      [attr.type]="inputType(field.fieldType)" />
                  }
                }
              </ain-form-field>
            }
          }
        </div>
      }

      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDialog()"></button>
        <button
          pButton
          type="button"
          [label]="t('common.save')"
          [disabled]="savingRecord()"
          (click)="saveRecord()"></button>
      </ng-template>
    </p-dialog>

    <p-confirmDialog />
    <p-toast />
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; text-decoration: none; font-weight: 600; }
    .filter-toggle { display: flex; }
    .filters { display: grid; gap: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 10px; }
    .filters h3 { margin: 0; font-size: 1rem; }
    .filter-row { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); align-items: end; }
    .filter-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .clickable-row { cursor: pointer; }
    .row-actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .record-form { display: grid; gap: 12px; max-height: min(70vh, 640px); overflow: auto; padding-inline-end: 4px; }
    .error { margin: 0; color: #b91c1c; }
    .card { background: var(--ain-color-surface, #fff); }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
  `]
})
export class DynamicRecordsComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly calendar = inject(CalendarService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  entityId = '';
  readonly pageSize = DEFAULT_PAGE_SIZE;

  readonly initialLoading = signal(true);
  readonly tableLoading = signal(false);
  readonly exporting = signal(false);
  readonly savingRecord = signal(false);
  readonly showFilters = signal(true);
  readonly entity = signal<DynamicEntityDetail | null>(null);
  readonly entityError = signal<string | null>(null);
  readonly listError = signal('');
  readonly records = signal<DynamicRecord[]>([]);
  readonly totalCount = signal(0);
  readonly tableFirst = signal(0);
  readonly sortField = signal<string | undefined>(undefined);
  readonly sortOrder = signal<number>(0);
  readonly filterRows = signal<FilterRow[]>([]);
  readonly lookupCache = signal<Map<string, LookupTargetCache>>(new Map());
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly editingRecordId = signal<string | null>(null);
  readonly formValues = signal<Record<string, unknown>>({});
  readonly fieldErrors = signal<Record<string, string>>({});

  recordDialogVisible = false;

  readonly sortedFields = computed(() =>
    [...(this.entity()?.fields ?? [])].sort((left, right) => left.displayOrder - right.displayOrder)
  );

  readonly tableColumns = computed(() => selectTableColumns(this.sortedFields()));

  readonly filterableFields = computed(() =>
    this.sortedFields().filter(field => !READ_ONLY_FIELD_TYPES.has(field.fieldType))
  );

  readonly pageTitle = computed(() => {
    const displayName = this.entity()?.displayName?.trim();
    return displayName ? `${this.t('dynamic.record.title')} — ${displayName}` : this.t('dynamic.record.title');
  });

  pageNumber = 1;
  private loadInFlight = false;
  private lastLazyFirst = -1;
  private lastLazySort = '';

  ngOnInit(): void {
    this.entityId = this.route.snapshot.paramMap.get('entityId') ?? '';
    if (!this.entityId) {
      this.entityError.set(this.t('dynamic.record.errorLoad'));
      this.initialLoading.set(false);
      return;
    }

    this.restoreStateFromQuery();
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.dynamicRecordsChanged))
      .subscribe(() => this.fetchRecords());

    void this.setupLiveSubscription();
    this.loadEntity();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  operatorNeedsValue = operatorNeedsValue;

  toggleFilters(): void {
    this.showFilters.update(value => !value);
    this.syncQueryParams();
  }

  addFilterRow(): void {
    this.filterRows.update(rows => [...rows, this.createFilterRow()]);
  }

  removeFilterRow(rowId: string): void {
    this.filterRows.update(rows => rows.filter(row => row.id !== rowId));
  }

  onFilterFieldChange(row: FilterRow): void {
    row.operator = row.fieldName ? operatorsForFieldType(this.fieldByName(row.fieldName)?.fieldType ?? 'Text')[0] ?? 'eq' : null;
    row.value = null;
    this.filterRows.set([...this.filterRows()]);
  }

  operatorOptions(fieldName: string | null): Array<{ value: DynamicRecordFilterOperator; label: string }> {
    const field = fieldName ? this.fieldByName(fieldName) : null;
    const operators = operatorsForFieldType(field?.fieldType ?? 'Text');
    return operators.map(value => ({
      value,
      label: this.t(`dynamic.record.operator.${value}`)
    }));
  }

  filterFieldType(fieldName: string): string {
    return this.fieldByName(fieldName)?.fieldType ?? 'Text';
  }

  fieldOptions(fieldName: string): DynamicFieldOption[] {
    return this.fieldByName(fieldName)?.options ?? [];
  }

  lookupOptionsForField(fieldName: string): DynamicFieldOption[] {
    const field = this.fieldByName(fieldName);
    if (!field?.lookupTargetEntityId) {
      return [];
    }
    return this.lookupCache().get(field.lookupTargetEntityId)?.options ?? [];
  }

  booleanOptions(): Array<{ label: string; value: boolean }> {
    return [
      { label: this.t('common.yes'), value: true },
      { label: this.t('common.no'), value: false }
    ];
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.tableFirst.set(0);
    this.lastLazyFirst = -1;
    this.syncQueryParams();
    this.fetchRecords();
  }

  clearFilters(): void {
    this.filterRows.set([]);
    this.applyFilters();
  }

  refreshRecords(): void {
    this.fetchRecords();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.loadInFlight || !this.entity()) {
      return;
    }

    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    const nextSortField = typeof event.sortField === 'string' ? event.sortField : undefined;
    const nextSortOrder = event.sortOrder ?? 0;
    const sortSignature = `${nextSortField ?? ''}:${nextSortOrder}`;

    if (first === this.lastLazyFirst && sortSignature === this.lastLazySort && this.records().length > 0) {
      return;
    }

    this.lastLazyFirst = first;
    this.lastLazySort = sortSignature;
    this.tableFirst.set(first);
    this.pageNumber = Math.floor(first / rows) + 1;
    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder === 1 || nextSortOrder === -1 ? nextSortOrder : 0);
    this.syncQueryParams();
    this.fetchRecords();
  }

  openCreateDialog(): void {
    if (!this.canCreateRecord()) {
      return;
    }

    this.dialogMode.set('create');
    this.editingRecordId.set(null);
    this.fieldErrors.set({});
    this.formValues.set(this.buildDefaultFormValues());
    this.loadLookupOptionsForEntity();
    this.recordDialogVisible = true;
  }

  openEditDialog(record: DynamicRecord): void {
    if (!this.canUpdateRecord()) {
      return;
    }

    this.dialogMode.set('edit');
    this.editingRecordId.set(record.id);
    this.fieldErrors.set({});
    this.formValues.set(this.buildFormValuesFromRecord(record));
    this.loadLookupOptionsForEntity();
    this.prefetchLookupLabelsFromRecord(record);
    this.recordDialogVisible = true;
  }

  closeDialog(): void {
    this.recordDialogVisible = false;
    this.editingRecordId.set(null);
    this.fieldErrors.set({});
  }

  saveRecord(): void {
    if (!this.validateRequiredFields()) {
      return;
    }

    const payload = this.buildValuesPayload();
    const recordId = this.editingRecordId();
    const request = this.dialogMode() === 'create'
      ? this.api.createRecord(this.entityId, { values: payload })
      : this.api.updateRecord(this.entityId, recordId!, { values: payload });

    this.savingRecord.set(true);
    request.pipe(
      finalize(() => this.savingRecord.set(false))
    ).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.record.saved') });
        this.closeDialog();
        this.fetchRecords();
      },
      error: error => this.handleSaveError(error)
    });
  }

  confirmDelete(record: DynamicRecord): void {
    if (!this.canDeleteRecord()) {
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.record.delete'),
      message: this.t('dynamic.record.confirmDelete'),
      accept: () => this.deleteRecord(record.id)
    });
  }

  exportCsv(): void {
    if (!this.canExportRecords() || this.exporting()) {
      return;
    }

    this.exporting.set(true);
    this.api.exportRecordsAndDownload(
      this.entityId,
      {
        sort: this.currentSort(),
        filter: this.buildApiFilters()
      },
      `${this.entity()?.name ?? 'records'}-${new Date().toISOString().slice(0, 10)}.csv`
    ).pipe(
      finalize(() => this.exporting.set(false))
    ).subscribe({
      next: () => this.messages.add({ severity: 'success', summary: this.t('dynamic.record.export') }),
      error: () => this.messages.add({ severity: 'error', summary: this.t('dynamic.record.exportFailed') })
    });
  }

  formatCellValue(record: DynamicRecord, field: DynamicField): string {
    const raw = record.values[field.name];
    if (raw === null || raw === undefined || raw === '') {
      return '—';
    }

    if (field.fieldType === 'Boolean') {
      return raw === true || raw === 'true' ? this.t('common.yes') : this.t('common.no');
    }

    if (DATE_FIELD_TYPES.has(field.fieldType)) {
      return this.calendarFormat(raw, field.fieldType === 'DateTime' ? 'datetime' : 'date');
    }

    if (field.fieldType === 'Select') {
      const option = field.options.find(item => item.value === `${raw}`);
      return option?.label ?? `${raw}`;
    }

    if (field.fieldType === 'MultiSelect') {
      const values = Array.isArray(raw) ? raw : `${raw}`.split(',').map(item => item.trim()).filter(Boolean);
      return values
        .map(value => field.options.find(item => item.value === `${value}`)?.label ?? `${value}`)
        .join(', ');
    }

    if (field.fieldType === 'Lookup') {
      return this.lookupLabel(field, `${raw}`);
    }

    if (READ_ONLY_FIELD_TYPES.has(field.fieldType)) {
      return `${raw}`.slice(0, 48);
    }

    return `${raw}`;
  }

  fieldLabel(field: DynamicField): string {
    return field.isRequired ? `${field.displayName} *` : field.displayName;
  }

  isReadOnlyField(field: DynamicField): boolean {
    return READ_ONLY_FIELD_TYPES.has(field.fieldType);
  }

  readOnlyValue(field: DynamicField): string {
    const value = this.formValues()[field.name];
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return `${value}`;
  }

  textMaxLength(field: DynamicField): number | null {
    const match = /(?:nvarchar|varchar)\((\d+)\)/i.exec(field.sqlDataType ?? '');
    return match ? Number(match[1]) : null;
  }

  inputType(fieldType: string): string {
    if (fieldType === 'Email') {
      return 'email';
    }
    if (fieldType === 'Url') {
      return 'url';
    }
    if (fieldType === 'Phone') {
      return 'tel';
    }
    return 'text';
  }

  inputMode(fieldType: string): string | null {
    if (fieldType === 'Email' || fieldType === 'Url') {
      return 'text';
    }
    if (fieldType === 'Phone') {
      return 'tel';
    }
    return null;
  }

  canViewRecords(): boolean {
    return this.hasRecordAction('View');
  }

  canCreateRecord(): boolean {
    return this.hasRecordAction('Create');
  }

  canUpdateRecord(): boolean {
    return this.hasRecordAction('Update');
  }

  canDeleteRecord(): boolean {
    return this.hasRecordAction('Delete');
  }

  canExportRecords(): boolean {
    return this.hasRecordAction('Export');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadEntity(): void {
    this.initialLoading.set(true);
    this.api.getEntity(this.entityId).pipe(
      finalize(() => this.initialLoading.set(false))
    ).subscribe({
      next: entity => {
        this.entity.set(entity);
        this.entityError.set(null);
        this.ensureInitialFilterRow();
        this.loadLookupOptionsForEntity();
        this.fetchRecords();
      },
      error: () => {
        this.entity.set(null);
        this.entityError.set(this.t('dynamic.record.errorLoad'));
      }
    });
  }

  private fetchRecords(): void {
    if (!this.entity() || this.loadInFlight) {
      return;
    }

    this.loadInFlight = true;
    this.tableLoading.set(true);
    this.listError.set('');

    this.api.listRecords(this.entityId, {
      page: this.pageNumber,
      pageSize: this.pageSize,
      sort: this.currentSort(),
      filter: this.buildApiFilters()
    }).pipe(
      finalize(() => {
        this.loadInFlight = false;
        this.tableLoading.set(false);
      })
    ).subscribe({
      next: page => {
        this.records.set(page.items);
        this.totalCount.set(page.totalCount);
        this.prefetchLookupLabelsFromRows(page.items);
      },
      error: () => {
        this.records.set([]);
        this.totalCount.set(0);
        this.listError.set(this.t('dynamic.record.errorLoad'));
      }
    });
  }

  private deleteRecord(recordId: string): void {
    this.api.deleteRecord(this.entityId, recordId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.record.deleted') });
        this.fetchRecords();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private handleSaveError(error: unknown): void {
    const issues = extractValidationIssues(error);
    if (issues.length > 0) {
      const nextErrors: Record<string, string> = {};
      for (const issue of issues) {
        const key = `dynamic.validation.${issue.errorCode}`;
        nextErrors[issue.fieldKey] = this.t(key);
      }
      this.fieldErrors.set(nextErrors);
    }

    this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') });
  }

  private validateRequiredFields(): boolean {
    const nextErrors: Record<string, string> = {};
    for (const field of this.sortedFields()) {
      if (!field.isRequired || this.isReadOnlyField(field)) {
        continue;
      }
      const value = this.formValues()[field.name];
      const empty = value === null
        || value === undefined
        || value === ''
        || (Array.isArray(value) && value.length === 0);
      if (empty) {
        nextErrors[field.name] = this.t('dynamic.validation.dynamic_field_required');
      }
    }
    this.fieldErrors.set(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  private buildValuesPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of this.sortedFields()) {
      if (this.isReadOnlyField(field)) {
        continue;
      }

      const raw = this.formValues()[field.name];
      if (raw === null || raw === undefined || raw === '') {
        payload[field.name] = null;
        continue;
      }

      switch (field.fieldType) {
        case 'Integer':
          payload[field.name] = Number(raw);
          break;
        case 'Decimal':
        case 'Money':
          payload[field.name] = Number(raw);
          break;
        case 'Boolean':
          payload[field.name] = raw === true || raw === 'true';
          break;
        case 'MultiSelect':
          payload[field.name] = Array.isArray(raw) ? raw : [`${raw}`];
          break;
        default:
          payload[field.name] = raw;
      }
    }
    return payload;
  }

  private buildDefaultFormValues(): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const field of this.sortedFields()) {
      if (field.fieldType === 'MultiSelect') {
        values[field.name] = [];
      } else if (field.fieldType === 'Boolean') {
        values[field.name] = field.defaultValue === 'true';
      } else if (field.fieldType === 'Date' || field.fieldType === 'DateTime') {
        values[field.name] = this.resolveDateDefaultValue(field.fieldType, field.defaultValue);
      } else {
        values[field.name] = field.defaultValue ?? null;
      }
    }
    return values;
  }

  private resolveDateDefaultValue(fieldType: string, defaultValue?: string | null): string | null {
    const normalized = defaultValue?.trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    if (normalized === NOW_DEFAULT_TOKEN) {
      const nowIso = new Date().toISOString();
      return fieldType === 'DateTime'
        ? nowIso.slice(0, 16)
        : nowIso.slice(0, 10);
    }

    return defaultValue ?? null;
  }

  private buildFormValuesFromRecord(record: DynamicRecord): Record<string, unknown> {
    const values = this.buildDefaultFormValues();
    for (const field of this.sortedFields()) {
      if (Object.prototype.hasOwnProperty.call(record.values, field.name)) {
        const raw = record.values[field.name];
        if (field.fieldType === 'MultiSelect') {
          values[field.name] = Array.isArray(raw)
            ? raw
            : typeof raw === 'string'
              ? raw.split(',').map(item => item.trim()).filter(Boolean)
              : raw == null
                ? []
                : [`${raw}`];
        } else {
          values[field.name] = raw;
        }
      }
    }
    return values;
  }

  private buildApiFilters(): DynamicRecordFilter[] {
    return this.filterRows()
      .filter(row => row.fieldName && row.operator)
      .filter(row => !operatorNeedsValue(row.operator!) || (row.value !== null && row.value !== ''))
      .map(row => ({
        field: row.fieldName!,
        operator: row.operator!,
        value: operatorNeedsValue(row.operator!) ? row.value : undefined
      }));
  }

  private currentSort(): DynamicRecordSort[] {
    const field = this.sortField();
    const order = this.sortOrder();
    if (!field || !order) {
      return [];
    }
    return [{ field, direction: order === 1 ? 'asc' : 'desc' }];
  }

  private loadLookupOptionsForEntity(): void {
    const targetIds = [...new Set(
      this.sortedFields()
        .filter(field => field.fieldType === 'Lookup' && field.lookupTargetEntityId)
        .map(field => field.lookupTargetEntityId!)
    )];

    for (const targetId of targetIds) {
      if (this.lookupCache().has(targetId)) {
        continue;
      }
      this.loadLookupTarget(targetId);
    }
  }

  private loadLookupTarget(targetEntityId: string): void {
    this.api.getEntity(targetEntityId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: targetEntity => {
        const labelField = pickLabelField(targetEntity.fields);
        this.api.listRecords(targetEntityId, { page: 1, pageSize: 200 }).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: page => {
            const options = page.items.map(record => ({
              value: record.id,
              label: buildRecordLabel(record, labelField, targetEntity.fields)
            }));
            this.lookupCache.update(cache => {
              const next = new Map(cache);
              next.set(targetEntityId, {
                labelFieldName: labelField?.name ?? null,
                options,
                labelById: new Map(options.map(option => [option.value, option.label]))
              });
              return next;
            });
          }
        });
      }
    });
  }

  private prefetchLookupLabelsFromRows(rows: DynamicRecord[]): void {
    for (const row of rows) {
      this.prefetchLookupLabelsFromRecord(row);
    }
  }

  private prefetchLookupLabelsFromRecord(record: DynamicRecord): void {
    for (const field of this.sortedFields()) {
      if (field.fieldType !== 'Lookup' || !field.lookupTargetEntityId) {
        continue;
      }
      const raw = record.values[field.name];
      if (raw === null || raw === undefined || raw === '') {
        continue;
      }
      const targetId = field.lookupTargetEntityId;
      const cache = this.lookupCache().get(targetId);
      if (cache?.labelById.has(`${raw}`)) {
        continue;
      }
      this.ensureLookupLabel(targetId, `${raw}`);
    }
  }

  private ensureLookupLabel(targetEntityId: string, recordId: string): void {
    this.api.getRecord(targetEntityId, recordId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: record => {
        this.api.getEntity(targetEntityId).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: entity => {
            const labelField = pickLabelField(entity.fields);
            const label = buildRecordLabel(record, labelField, entity.fields);
            this.lookupCache.update(cache => {
              const existing = cache.get(targetEntityId) ?? {
                labelFieldName: labelField?.name ?? null,
                options: [],
                labelById: new Map<string, string>()
              };
              const labelById = new Map(existing.labelById);
              labelById.set(recordId, label);
              const options = existing.options.some(option => option.value === recordId)
                ? existing.options
                : [...existing.options, { value: recordId, label }];
              const next = new Map(cache);
              next.set(targetEntityId, {
                labelFieldName: existing.labelFieldName,
                options,
                labelById
              });
              return next;
            });
          }
        });
      }
    });
  }

  private lookupLabel(field: DynamicField, rawValue: string): string {
    const targetId = field.lookupTargetEntityId;
    if (!targetId) {
      return rawValue.slice(0, 8);
    }
    const cached = this.lookupCache().get(targetId)?.labelById.get(rawValue);
    if (cached) {
      return cached;
    }
    this.ensureLookupLabel(targetId, rawValue);
    return rawValue.slice(0, 8);
  }

  private calendarFormat(value: unknown, mode: 'date' | 'datetime'): string {
    if (mode === 'datetime') {
      return this.calendar.formatDate(value as string | Date | number | null | undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return this.calendar.formatDate(value as string | Date | number | null | undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  private fieldByName(name: string): DynamicField | undefined {
    return this.sortedFields().find(field => field.name === name);
  }

  private createFilterRow(): FilterRow {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fieldName: null,
      operator: null,
      value: null
    };
  }

  private ensureInitialFilterRow(): void {
    if (this.filterRows().length === 0) {
      this.filterRows.set([this.createFilterRow()]);
    }
  }

  private restoreStateFromQuery(): void {
    const params = this.route.snapshot.queryParamMap;
    this.showFilters.set((params.get('showFilters') ?? '1') !== '0');

    const page = Number(params.get('page') ?? '1');
    this.pageNumber = Number.isFinite(page) && page > 0 ? page : 1;
    this.tableFirst.set((this.pageNumber - 1) * this.pageSize);
    this.lastLazyFirst = this.tableFirst() - 1;

    const sortField = params.get('sortField') ?? undefined;
    const sortDir = params.get('sortDir');
    this.sortField.set(sortField || undefined);
    this.sortOrder.set(sortDir === 'asc' ? 1 : sortDir === 'desc' ? -1 : 0);
    this.lastLazySort = `${sortField ?? ''}:${this.sortOrder() ?? 0}`;

    const filtersJson = params.get('filters');
    if (filtersJson) {
      try {
        const parsed = JSON.parse(filtersJson) as FilterRow[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.filterRows.set(parsed);
        }
      } catch {
        this.filterRows.set([this.createFilterRow()]);
      }
    }
  }

  private syncQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.currentQueryParams(),
      replaceUrl: true
    });
  }

  private currentQueryParams(): Record<string, string> {
    const params: Record<string, string> = {
      showFilters: this.showFilters() ? '1' : '0',
      page: String(this.pageNumber)
    };

    if (this.sortField() && this.sortOrder()) {
      params['sortField'] = this.sortField()!;
      params['sortDir'] = this.sortOrder() === 1 ? 'asc' : 'desc';
    }

    const activeFilters = this.filterRows().filter(row => row.fieldName && row.operator);
    if (activeFilters.length > 0) {
      params['filters'] = JSON.stringify(activeFilters);
    }

    return params;
  }

  private hasRecordAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.records');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId || !this.entityId) return;

    this.subscribedTopic = dynamicEntityRecordsTopic(tenantId, this.entityId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
